import React, { useEffect, useState } from "react";
import { investorApi, vendorApi } from "../../api/api";
import { useSelector } from "react-redux";

import {
  Box,
  Grid,
  Modal,
  IconButton,
  Snackbar,
  Alert,
  Typography,
  Chip,
  Button,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/zoom";

import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Keyboard, Zoom } from "swiper/modules";

import YourPropertyDetails from "./YourPropertyDetails";

const YourPropertyImages = ({ data }) => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);

  const [modalOpen, setModalOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  const [showControls, setShowControls] = useState(true);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const theme = useSelector((state) => state.themeValue.colors);

  useEffect(() => {
    fetchImages();
  }, [data[0].id]);

  const fetchImages = async () => {
    try {
      const response = await investorApi.getImagesByPropertyId(data[0].id);

      if (response.status === 200 && Array.isArray(response.data)) {
        setImages(response.data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const openModal = (index = 0) => {
    setZoomLevel(1); // reset zoom
    setCurrentIndex(index);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  // ----------------------------
  // ❗ FIXED MISSING FUNCTIONS
  // ----------------------------

  const handleZoomIn = () => {
    setZoomLevel((prev) => Math.min(prev + 0.2, 3));
  };

  const handleZoomOut = () => {
    setZoomLevel((prev) => Math.max(prev - 0.2, 0.5));
  };

  const toggleFullscreen = () => {
    setIsFullscreen((prev) => !prev);
  };

  const handleDownload = (url) => {
    const link = document.createElement("a");
    link.href = url;
    link.download = "property-image.jpg";
    link.click();
  };

  const handleDelete = async (imageId) => {
    try {
      await vendorApi.deleteImageById({ type: data[0].type, id: imageId });
      setSnackbar({
        open: true,
        message: "Image deleted successfully",
        severity: "success",
      });

      fetchImages();
    } catch (err) {
      setSnackbar({
        open: true,
        message: "Failed to delete image",
        severity: "error",
      });
    }
  };

  // ----------------------------
  // UTILITY
  // ----------------------------
  const getRightImageHeight = (totalImages) => {
    if (totalImages === 3) return "50%";
    if (totalImages === 4) return "33.33%";
    return "50%";
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="70px"
        bgcolor="#eee"
        borderRadius={1}
      >
        Loading...
      </Box>
    );
  }

  if (!images.length) {
    return (
      <Box textAlign="center" p={2} color="#666">
        No images available
      </Box>
    );
  }

  return (
    <Box width="100%" mt={2} minHeight="80vh" position="relative">
      {/* --------------------------
      MAIN GRID DISPLAY
      --------------------------- */}
      <Box display="flex" width="100%" height={{ xs: "45vh", sm: "50vh" }} gap={1}>
        {images[0] && (
          <Box
            flex={1}
            height="100%"
            borderRadius={2}
            overflow="hidden"
            onClick={() => openModal(0)}
            sx={{ cursor: "pointer" }}
          >
            <img
              src={images[0].ImageURL}
              alt="main"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                borderRadius: "10px",
              }}
            />
          </Box>
        )}

        <Box flex={1} display="flex" flexDirection="column" gap={1}>
          {images.slice(1, 5).map((img, index) => {
            const height = getRightImageHeight(images.length);
            const showOverlay = index === 3 && images.length > 5;

            return (
              <Box
                key={img.id}
                height={height}
                borderRadius={2}
                overflow="hidden"
                position="relative"
                onClick={() => openModal(index + 1)}
                sx={{ cursor: "pointer" }}
              >
                <img
                  src={img.ImageURL}
                  alt={`thumb-${index}`}
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />

                {showOverlay && (
                  <Box
                    position="absolute"
                    top={0}
                    left={0}
                    width="100%"
                    height="100%"
                    bgcolor="rgba(0,0,0,0.5)"
                    color="#fff"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    fontSize={20}
                    fontWeight="bold"
                    borderRadius={2}
                  >
                    +{images.length - 4} more
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* PROPERTY DETAILS */}
      <Box width="100%" position="absolute" bottom={{ xs: 20, sm: 15 }}>
        <YourPropertyDetails data={data} />
      </Box>

      {/* --------------------------
      FULL SCREEN MODAL VIEWER
      --------------------------- */}
      <Modal
        open={modalOpen}
        onClose={closeModal}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backdropFilter: "blur(12px)",
        }}
      >
        <Box
          sx={{
            width: isFullscreen ? "100vw" : "95vw",
            height: isFullscreen ? "100vh" : "95vh",
            bgcolor: "rgba(0,0,0,0.95)",
            borderRadius: 2,
            overflow: "hidden",
            position: "relative",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* HEADER BAR */}
          {showControls && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: 60,
                bgcolor: "rgba(0,0,0,0.7)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 3,
                zIndex: 10,
              }}
            >
              <Typography variant="h6" color="#fff">
                {data[0]?.name || "Property Images"}
              </Typography>

              <Box display="flex" gap={1}>
                <Chip
                  label={`${currentIndex + 1} / ${images.length}`}
                  size="small"
                  sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "white" }}
                />

                <IconButton onClick={closeModal} sx={{ color: "white" }}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </Box>
          )}

          {/* IMAGE VIEWER */}
          <Box
            flex={1}
            display="flex"
            alignItems="center"
            justifyContent="center"
            position="relative"
          >
            <Swiper
              modules={[Navigation, Pagination, Keyboard, Zoom]}
              initialSlide={currentIndex}
              loop
              navigation={{
                nextEl: ".swiper-button-next-custom",
                prevEl: ".swiper-button-prev-custom",
              }}
              pagination={{ clickable: true, type: "fraction" }}
              keyboard={{ enabled: true }}
              zoom={{ maxRatio: 3 }}
              onSlideChange={(swiper) => setCurrentIndex(swiper.activeIndex)}
              style={{ width: "100%", height: "100%" }}
            >
              {images.map((img, idx) => (
                <SwiperSlide key={idx}>
                  <div className="swiper-zoom-container">
                    <img
                      src={img.ImageURL}
                      alt={`Slide-${idx}`}
                      style={{
                        maxWidth: "100%",
                        maxHeight: "100%",
                        objectFit: "contain",
                        transform: `scale(${zoomLevel})`,
                        transition: "0.3s",
                      }}
                    />
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>

            {/* LEFT / RIGHT BUTTONS */}
            <IconButton
              className="swiper-button-prev-custom"
              sx={{
                position: "absolute",
                left: 15,
                top: "50%",
                transform: "translateY(-50%)",
                bgcolor: "rgba(0,0,0,0.4)",
                color: "white",
              }}
            >
              <ArrowBackIosIcon />
            </IconButton>

            <IconButton
              className="swiper-button-next-custom"
              sx={{
                position: "absolute",
                right: 15,
                top: "50%",
                transform: "translateY(-50%)",
                bgcolor: "rgba(0,0,0,0.4)",
                color: "white",
              }}
            >
              <ArrowForwardIosIcon />
            </IconButton>
          </Box>

          {/* FOOTER CONTROLS */}
          {showControls && (
            <Box
              sx={{
                height: 70,
                bgcolor: "rgba(0,0,0,0.85)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 3,
              }}
            >
              {/* ZOOM */}
              <Box display="flex" gap={1}>
                <IconButton
                  onClick={handleZoomOut}
                  disabled={zoomLevel <= 0.5}
                  sx={{ color: "white" }}
                >
                  <ZoomOutIcon />
                </IconButton>

                <Chip
                  label={`${Math.round(zoomLevel * 100)}%`}
                  size="small"
                  sx={{ color: "white" }}
                />

                <IconButton
                  onClick={handleZoomIn}
                  disabled={zoomLevel >= 3}
                  sx={{ color: "white" }}
                >
                  <ZoomInIcon />
                </IconButton>
              </Box>

              {/* DOWNLOAD */}
              <Button
                startIcon={<DownloadIcon />}
                variant="contained"
                size="small"
                onClick={() => handleDownload(images[currentIndex]?.ImageURL)}
              >
                Download
              </Button>

              {/* FULLSCREEN + DELETE */}
              <Box display="flex" gap={1}>
                <IconButton onClick={toggleFullscreen} sx={{ color: "white" }}>
                  {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                </IconButton>

                <IconButton
                  onClick={() => handleDelete(images[currentIndex]?.id)}
                  sx={{ color: "red" }}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            </Box>
          )}
        </Box>
      </Modal>

      {/* SNACKBAR */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default YourPropertyImages;
